import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import http, { formatApiErrorDetail } from "@/api";
import { useAuth } from "@/auth";
import KpiProposalsPanel from "@/components/KpiProposalsPanel";
import { CheckCircle2, XCircle } from "lucide-react";

export default function Inbox() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [crs, setCrs] = useState([]);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (user && user.role !== "admin") { nav("/dashboard"); return; }
    http.get("/admin/change-requests", { params: { status: "pending" } })
      .then((r) => setCrs(r.data))
      .catch(() => {});
  }, [user, nav]);

  const resolveCr = async (id, action) => {
    let note = "";
    if (action === "reject") {
      note = window.prompt("Motivo del rechazo (se enviará al solicitante):") || "";
      if (!note) return;
    } else {
      note = window.prompt("Nota para el solicitante (opcional):") || "";
    }
    setBusy(id); setErr("");
    try {
      await http.put(`/admin/change-requests/${id}`, { action, note });
      const { data } = await http.get("/admin/change-requests", { params: { status: "pending" } });
      setCrs(data);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <div className="mb-6">
        <div className="font-hand text-xl text-[var(--lala-blue)]">gobernanza</div>
        <h1 className="font-display text-4xl">Aprobaciones y solicitudes</h1>
        <p className="text-[var(--ink-soft)] mt-1">
          Las evaluaciones normales se guardan directamente. El Admin interviene únicamente en excepciones de Objetivo y solicitudes de cambio que requieren decisión explícita.
        </p>
      </div>

      {err && (
        <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
          {err}
        </div>
      )}

      <KpiProposalsPanel />

      <div className="mt-10">
        <div className="font-hand text-xl text-[var(--lala-blue)]">gobernanza</div>
        <h2 className="font-display text-2xl mb-3">Solicitudes de cambio</h2>
        {crs.length === 0 ? (
          <div className="sketch-border-soft bg-[var(--cream-soft)] p-6 text-sm text-[var(--ink-soft)]" data-testid="cr-empty">
            No hay solicitudes de cambio pendientes.
          </div>
        ) : (
          <div className="space-y-3" data-testid="cr-list">
            {crs.map((c) => (
              <div key={c.id} className="sketch-border bg-white p-4" data-testid={`cr-row-${c.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-[var(--lala-blue)] font-bold">
                      {new Date(c.created_at).toLocaleString("es-MX")} · {c.requested_by?.name} ({c.requested_by?.role})
                    </div>
                    <div className="font-display text-lg">
                      <Link to={`/proveedores/${c.supplier_id}`} className="text-[var(--lala-blue)] hover:underline">
                        {c.supplier_name}
                      </Link>
                      {c.subject ? <span className="text-sm text-[var(--ink-soft)] ml-2">· {c.subject}</span> : null}
                    </div>
                    <p className="text-sm text-[var(--ink-soft)] mt-1">{c.justification}</p>
                  </div>
                  <div className="shrink-0 flex gap-2">
                    <button
                      disabled={busy === c.id}
                      onClick={() => resolveCr(c.id, "approve")}
                      className="text-green-700 hover:underline inline-flex items-center gap-1 text-sm"
                      data-testid={`cr-approve-${c.id}`}
                    >
                      <CheckCircle2 size={14} /> Aprobar
                    </button>
                    <button
                      disabled={busy === c.id}
                      onClick={() => resolveCr(c.id, "reject")}
                      className="text-red-700 hover:underline inline-flex items-center gap-1 text-sm"
                      data-testid={`cr-reject-${c.id}`}
                    >
                      <XCircle size={14} /> Rechazar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
